import { isNative } from '@/lib/capacitor'
import { PushNotifications } from '@capacitor/push-notifications'
import { supabase } from '@/lib/supabase'
import { captureException } from '@/lib/posthog'

const RETRY_DELAY_MS = 5000

function getBundleId(): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
  if (supabaseUrl.includes('kxheumzevhqeqkjcvspe')) {
    return 'com.zestdent.zrm.dev'
  }
  return 'com.zestdent.zrm'
}

async function registerTokenWithBackend(
  deviceToken: string,
  retried = false
): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('register-push-token', {
      body: {
        device_token: deviceToken,
        bundle_id: getBundleId(),
      },
    })
    if (error) throw error
  } catch (err) {
    console.error('Failed to register push token:', err)
    if (err instanceof Error) {
      captureException(err, { source: 'push_token_registration', retried })
    }
    if (!retried) {
      setTimeout(() => registerTokenWithBackend(deviceToken, true), RETRY_DELAY_MS)
    }
  }
}

export async function initPushNotifications(): Promise<void> {
  if (!isNative()) return

  try {
    await PushNotifications.addListener('registration', (token) => {
      registerTokenWithBackend(token.value)
    })

    await PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error)
      const apnsError = new Error(
        typeof error === 'string' ? error : (error as { error?: string }).error || 'APNs registration error'
      )
      captureException(apnsError, { source: 'push_registration_apns' })
      setTimeout(async () => {
        try {
          await PushNotifications.register()
        } catch (retryErr) {
          console.error('Push registration retry failed:', retryErr)
          if (retryErr instanceof Error) {
            captureException(retryErr, { source: 'push_registration_retry' })
          }
        }
      }, RETRY_DELAY_MS)
    })

    const permResult = await PushNotifications.requestPermissions()
    if (permResult.receive === 'granted') {
      await PushNotifications.register()
    }
  } catch (err) {
    console.error('Push notification init error:', err)
    if (err instanceof Error) {
      captureException(err, { source: 'push_notification_init' })
    }
  }
}
